import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {PaylinkSubscription} from '../../../models/paylink-subscription';
import {MerchantPackage} from '../../../models/merchant-package';
import {Router, RouterModule} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ChangeMerchantPackageRequest} from '../../../models/change-merchant-package-request';
import {MyBusiness} from '../../../models/my-business';
import {EnvironmentService} from '../../../services/environment.service';
import {ApiAgentService} from '../../../services/api-agent.service';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

declare var Snackbar: any;

export enum PaymentPlan {
  MonthlyPlan = 'MonthlyPlan',
  YearlyPlan = 'YearlyPlan',
  Free = 'Free',
}

export enum PaymentVia {
  MerchantWallet = 'MerchantWallet',
  BankCard = 'BankCard',
  NA = 'NA'
}

@Component({
  selector: 'app-package-panel',
  templateUrl: './package-panel.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, RouterModule],
  styleUrls: ['./package-panel.component.scss']
})
export class PackagePanelComponent implements OnInit {

  @Input('myBusiness') myBusiness: MyBusiness;
  @Input('lang') lang: any;
  @Input('submitting') submitting: any;
  @Input('isSelecting') isSelecting: boolean;
  @Input('isPaying') isPaying: boolean;
  @Input('isRenew') isRenew: boolean;
  @Input('message') message: string;
  @Input('wallet') wallet: number;
  @Input('isCurrentSubscription') isCurrentSubscription: boolean;
  @Input('paylinkSubscription') paylinkSubscription: PaylinkSubscription;
  @Input('merchantPackage') merchantPackage: MerchantPackage;
  @Output('packageSelected') packageSelected = new EventEmitter<any>();
  @Output('paymentSubmitted') paymentSubmitted = new EventEmitter<ChangeMerchantPackageRequest>();

  paymentPlan: PaymentPlan;
  paymentVia: PaymentVia;
  approveTerms: any;

  isAccountant: boolean;
  isManager: boolean;
  isSales: boolean;
  isMerchant: boolean;
  oldSetupFees: any;

  constructor(private router: Router, private api: ApiAgentService, private translate: TranslateService, private env: EnvironmentService) {
  }

  // @ts-ignore
  get canPayUsingWallet(): boolean {
    if (this.wallet >= 0 && this.totalAmount) {
      // return this.wallet >= this.totalAmount; // allow only wallet when it is more than the total amount.
      return this.wallet >= 0; // allow wallet if it is more than or equal to zero
    } else {
      return false;
    }
  }

  // @ts-ignore
  get totalAmount() {
    if (this.paymentPlan == PaymentPlan.YearlyPlan) {
      return this.isRenew ? this.paylinkSubscription.yearlyFees : this.paylinkSubscription.setupFees + this.paylinkSubscription.yearlyFees;
    } else {
      return this.isRenew ? this.paylinkSubscription.monthlyFees : this.paylinkSubscription.setupFees + this.paylinkSubscription.monthlyFees;
    }
  }

  // @ts-ignore
  get totalAmountIncludeVat() {
    let vat = this.vatAmount;
    let ttl = this.totalAmount;
    let rslt = Math.round(this.pad(ttl + vat));
    return rslt;
  }

  // @ts-ignore
  get vatAmount() {
    if (this.paymentPlan == PaymentPlan.YearlyPlan) {
      if (this.isRenew) {
        return this.paylinkSubscription.yearlyFees * (this.myBusiness.paylinkVat != null ? this.myBusiness.paylinkVat : 0);
      } else {
        return (this.paylinkSubscription.setupFees + this.paylinkSubscription.yearlyFees) * (this.myBusiness.paylinkVat != null ? this.myBusiness.paylinkVat : 0);
      }
    } else {
      if (this.isRenew) {
        return this.paylinkSubscription.monthlyFees * (this.myBusiness.paylinkVat != null ? this.myBusiness.paylinkVat : 0);
      } else {
        return (this.paylinkSubscription.setupFees + this.paylinkSubscription.monthlyFees) * (this.myBusiness.paylinkVat != null ? this.myBusiness.paylinkVat : 0);
      }
    }
  }

  // @ts-ignore
  get weeklyTransfers() {
    let i = 0;
    if (this.paylinkSubscription.transferInSun) {
      i++;
    }
    if (this.paylinkSubscription.transferInMon) {
      i++;
    }
    if (this.paylinkSubscription.transferInThu) {
      i++;
    }
    if (this.paylinkSubscription.transferInWed) {
      i++;
    }
    if (this.paylinkSubscription.transferInTue) {
      i++;
    }
    if (this.paylinkSubscription.transferInSat) {
      i++;
    }
    return i;
  }

  // @ts-ignore
  get daysRemaining(): number {
    if (this.merchantPackage.endDate) {
      return this.calculateDiff(this.merchantPackage.endDate);
    } else {
      return null;
    }
  }

  checkPermission() {
    let perms = this.env.checkPermission(this.myBusiness.subAccountType, this.myBusiness.subAccountGovId);
    this.isAccountant = perms.isAccountant;
    this.isSales = perms.isSales;
    this.isManager = perms.isManager;
    this.isMerchant = perms.isMerchant;
  }

  isStringOrTimestamp(variable) {
    // First, check if the variable is a string
    if (typeof variable === 'string') {
      return 'string';
    }

    // Next, check if the variable is a timestamp (which will be a number)
    if (typeof variable === 'number') {
      // If the number is 13 digits long, assume it's a timestamp
      return 'number';
    }

    // If the variable is not a string or a timestamp, return false
    return false;
  }

  getDateString(dateSent) {
    let date = dateSent;
    dateSent = new Date(dateSent);
    if (isNaN(dateSent) && date.indexOf('00:00:00') > -1) {
      dateSent = Date.parse(date.replace('00:00:00', '').trim());
      dateSent = new Date(dateSent);
    }
    return dateSent;
  }

  calculateDiff(dateSent) {
    let currentDate = new Date();
    dateSent = this.getDateString(dateSent);
    if (currentDate < dateSent) {
      return Math.abs(Math.floor((Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) - Date.UTC(dateSent.getFullYear(), dateSent.getMonth(), dateSent.getDate())) / (1000 * 60 * 60 * 24)));
    } else {
      return 0;
    }
  }

  async changePackage() {
    if (this.isRenew) {
      await this.router.navigate(['/private', 'settings']);
    } else {
      await this.router.navigate(['/private', 'choose-subscription']);
    }
  }

  async ngOnInit() {
    if (this.isPaying && this.paylinkSubscription.monthlyFees == 0 && this.paylinkSubscription.yearlyFees > 0) {
      this.paymentPlan = PaymentPlan.YearlyPlan;
    } else if (this.isPaying) {
      this.paymentPlan = PaymentPlan.MonthlyPlan;
    }
    this.checkPermission();

    if (this.paylinkSubscription.setupFees > 0) {
      this.api.getWaiveSetupFees().then((data: { waiveSetupFees: any }) => {
        console.log('data.waiveSetupFees', data.waiveSetupFees);
        if (data.waiveSetupFees == true || data.waiveSetupFees === 'true') {
          this.oldSetupFees = this.paylinkSubscription.setupFees;
          this.paylinkSubscription.setupFees = 0;

          console.log('this.paylinkSubscription.setupFees', this.paylinkSubscription.setupFees);
          console.log('this.oldSetupFees', this.oldSetupFees);
        }
      });
    }
  }

  async submitPayment() {
    if (!this.approveTerms && this.paylinkSubscription.subscriptionType != 'marketing') {
      let selectPaymentViaError = await this.translate.get('Please approve terms').toPromise();
      Snackbar.show({
        text: selectPaymentViaError,
        pos: 'bottom-center'
      });
      return;
    }

    // if (this.totalAmount > 0 && this.totalAmount == this.paylinkSubscription.setupFees) {
    if (this.totalAmount > 0 && this.totalAmount == this.paylinkSubscription.setupFees) {
      if (this.paymentVia) {
        this.paymentSubmitted.emit({
          subscriptionId: this.paylinkSubscription.id,
          paymentPlan: PaymentPlan.Free,
          paymentVia: this.paymentVia,
          amount: this.totalAmountIncludeVat,
        });
      } else {
        let selectPaymentViaError = await this.translate.get('Please select payment method').toPromise();
        Snackbar.show({
          text: selectPaymentViaError,
          pos: 'bottom-center'
        });
      }
    } else if (this.totalAmount > 0) {
      if (this.paymentVia) {
        this.paymentSubmitted.emit({
          subscriptionId: this.paylinkSubscription.id,
          paymentPlan: this.paymentPlan,
          paymentVia: this.paymentVia,
          amount: this.totalAmountIncludeVat,
        });
      } else {
        let selectPaymentViaError = await this.translate.get('Please select payment method').toPromise();
        Snackbar.show({
          text: selectPaymentViaError,
          pos: 'bottom-center'
        });
      }
    } else {
      this.paymentSubmitted.emit({
        subscriptionId: this.paylinkSubscription.id,
        paymentPlan: PaymentPlan.Free,
        paymentVia: PaymentVia.NA,
        amount: 0,
      });
    }

  }

  async submitPackage() {
    this.packageSelected.emit(this.paylinkSubscription.id);
  }

  async renewPackage() {
    await this.router.navigate(['/private', 'renew-subscription']);
  }

  private pad(number): number {
    number = number * 100;
    number = Math.round(number);
    return number / 100;
  }
}
